import {
  AirRingColors,
  FrontendBackendRingNameMap,
  FrontendBackendStoresMap,
} from 'content/products/ring';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';

export type RingPurchaseCodeApiResponse = {
  code_valid: boolean;
  message?: string;
  source?: string;
  color_flow?: string;
  source_display_name?: string;
  shopify_store?: string | null;
  default_color?: string;
  disabled_skus?: string[];
  /**
   * Purchase-code spelling: `air` or `ring_pro`.
   * A missing value means AIR. The PRO claim page requires `ring_pro`.
   * Ring order status spells AIR as `r1_air`, not `air`.
   */
  ring_model?: string | null;
  /** Plug: PRO color ids the code may use. Null means the page's full set. */
  available_colors?: string[] | null;
  shipping_disclaimer?: string;
  selection_disclaimer?: string;
  selection_disclaimer_valid_colors?: string[];
};

export type RingClaimModelGate =
  | { kind: 'continue' }
  | {
      kind: 'stop';
      /** Set only for an exact cross-product code. */
      wrongProductHref: '/ring/claim' | '/ring-pro/claim' | null;
      error: 'purchaseCodeForAir' | 'purchaseCodeForPro' | 'fallback';
    };

/**
 * PRO opens the order form only when `ring_model` is exactly `ring_pro`.
 * Anything else stays on the code step: exact `air` links to the AIR claim
 * page, and a missing or unexpected value is a generic error.
 * AIR opens for `air`, and for a missing or unexpected value. Exact
 * `ring_pro` links to the PRO claim page.
 */
export function ringClaimModelGate(
  ringModel: string | null | undefined,
  product: 'air' | 'ring_pro',
): RingClaimModelGate {
  if (product === 'ring_pro') {
    if (ringModel === 'ring_pro') return { kind: 'continue' };
    if (ringModel === 'air') {
      return {
        kind: 'stop',
        wrongProductHref: '/ring/claim',
        error: 'purchaseCodeForAir',
      };
    }
    return { kind: 'stop', wrongProductHref: null, error: 'fallback' };
  }

  if (ringModel === 'ring_pro') {
    return {
      kind: 'stop',
      wrongProductHref: '/ring-pro/claim',
      error: 'purchaseCodeForPro',
    };
  }
  return { kind: 'continue' };
}

export type RingPurchaseCodeSession = {
  token: string;
  store: ShopifyStore;
  source: string | null;
  colorFlow: string | null;
  sourceDisplayName: string | null;
  defaultColor: string | null;
  /** Backend color ids, when the API sends `available_colors`. Null if omitted. */
  availableColorIds: string[] | null;
  colorSelectionDisabled: boolean;
  disabledSkus: string[];
  shippingDisclaimer: string | null;
  selectionDisclaimer: string | null;
  selectionDisclaimerValidColors: string[];
};

export type RingClaimUiConfig = {
  showRoseGoldNewBadge: boolean;
  showRoseGoldRefundDisclaimer: boolean;
  submitButtonKey: 'payNow' | 'placeOrder';
};

export type ParseRingPurchaseCodeOptions = {
  ipCountry?: string;
  defaultColorByPage?: string;
  /** Map a backend color id onto this page's color key. AIR uses the AIR map. */
  fromBackendColor?: (backendColorId: string) => string | null;
};

/**
 * Placeholder for purchase-code `shopify_store` values that are not in
 * FrontendBackendStoresMap yet. Add a row when backend confirms the store.
 * Unknown ids stay ROW, matching the existing AIR claim behavior.
 * Backend confirmed the full set on 2026-09-28 (stelcore, us, eu_uk, india,
 * uae) and all of it is mapped, so this is empty until a new store appears.
 */
const CLAIM_STORE_PLUGS: Partial<Record<string, ShopifyStore>> = {};

function resolveStore(
  shopifyStore: string | null | undefined,
  ipCountry?: string,
): ShopifyStore {
  if (!shopifyStore) {
    return ShopifyStore.ROW;
  }

  const plugged = CLAIM_STORE_PLUGS[shopifyStore];
  if (plugged) return plugged;

  let store = FrontendBackendStoresMap.getKey(shopifyStore) as ShopifyStore;

  if (shopifyStore === 'eu_uk' && ipCountry) {
    store = ipCountry === 'GB' ? ShopifyStore.GB : ShopifyStore.EU;
  }

  return store ?? ShopifyStore.ROW;
}

function resolveDefaultColor(
  data: RingPurchaseCodeApiResponse,
  options: ParseRingPurchaseCodeOptions,
): { defaultColor: string | null; colorSelectionDisabled: boolean } {
  if (options.defaultColorByPage) {
    return {
      defaultColor: options.defaultColorByPage,
      colorSelectionDisabled: true,
    };
  }

  if (data.default_color) {
    if (options.fromBackendColor) {
      const mapped = options.fromBackendColor(data.default_color);
      if (mapped) {
        return { defaultColor: mapped, colorSelectionDisabled: true };
      }
      return { defaultColor: null, colorSelectionDisabled: false };
    }

    const defaultColor = FrontendBackendRingNameMap.getKey(data.default_color);
    if (defaultColor && defaultColor in AirRingColors) {
      return {
        defaultColor: defaultColor as AirRingColors,
        colorSelectionDisabled: true,
      };
    }
  }

  return { defaultColor: null, colorSelectionDisabled: false };
}

export function parseRingPurchaseCodeResponse(
  data: RingPurchaseCodeApiResponse,
  token: string,
  options: ParseRingPurchaseCodeOptions = {},
): RingPurchaseCodeSession | null {
  if (!data.code_valid) {
    return null;
  }

  const { defaultColor, colorSelectionDisabled } = resolveDefaultColor(
    data,
    options,
  );

  return {
    token,
    store: resolveStore(data.shopify_store, options.ipCountry),
    source: data.source ?? null,
    colorFlow: data.color_flow ?? null,
    sourceDisplayName: data.source_display_name ?? null,
    defaultColor,
    colorSelectionDisabled,
    availableColorIds: Array.isArray(data.available_colors)
      ? data.available_colors
      : null,
    disabledSkus: Array.isArray(data.disabled_skus) ? data.disabled_skus : [],
    shippingDisclaimer: data.shipping_disclaimer || null,
    selectionDisclaimer: data.selection_disclaimer || null,
    selectionDisclaimerValidColors: Array.isArray(
      data.selection_disclaimer_valid_colors,
    )
      ? data.selection_disclaimer_valid_colors
      : [],
  };
}

function getBaseRingClaimUi(
  session: RingPurchaseCodeSession,
  selectedColor?: string,
): RingClaimUiConfig {
  const isRoseGold = selectedColor === AirRingColors.ROSE_GOLD;

  return {
    showRoseGoldNewBadge: true,
    showRoseGoldRefundDisclaimer: isRoseGold && !!session.sourceDisplayName,
    submitButtonKey: isRoseGold ? 'payNow' : 'placeOrder',
  };
}

const CLAIM_UI_RULES: Array<{
  match: (session: RingPurchaseCodeSession) => boolean;
  patch: Partial<RingClaimUiConfig>;
}> = [
  {
    match: (session) =>
      session.colorFlow === 'brg_only' &&
      (session.source === 'veepee' || session.source === 'qoqa'),
    patch: {
      showRoseGoldNewBadge: false,
      showRoseGoldRefundDisclaimer: false,
      submitButtonKey: 'placeOrder',
    },
  },
];

export function resolveRingClaimUi(
  session: RingPurchaseCodeSession,
  selectedColor?: string,
): RingClaimUiConfig {
  return CLAIM_UI_RULES.reduce(
    (config, rule) =>
      rule.match(session) ? { ...config, ...rule.patch } : config,
    getBaseRingClaimUi(session, selectedColor),
  );
}
